import {DatePipe} from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {ReportService} from '@gatewee/services/report.service';
import {TaskService} from '@gatewee/services/task.service';
import {
  TaskScope,
  TaskStatus,
  TaskType,
  TaskWithProgress,
} from '@gatewee/types/task';
import {
  getTaskLabel,
  getTaskSubtitle,
} from '@gatewee/utils/helpers/get-task-label';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {GateweeAlertDialogComponent} from '../gatewee-alert-dialog/gatewee-alert-dialog.component';
import {catchError, of, Subject, switchMap, takeUntil} from 'rxjs';
import {pollWhileVisible} from '@gatewee/utils/helpers/poll-while-visible';
import {GateweeTableV2Component} from '../gatewee-table-v2/gatewee-table-v2.component';

type HistoryStatusFilter = 'all' | 'completed' | 'failed';

@Component({
  selector: 'gatewee-tasks',
  templateUrl: './gatewee-tasks.component.html',
  styleUrls: ['./gatewee-tasks.component.css'],
  standalone: false,
  providers: [DatePipe],
})
export class GateweeTasksComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({required: true}) scope!: TaskScope;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() disableContentPage = false;

  @ViewChild('taskTpl', {read: TemplateRef})
  taskTpl!: TemplateRef<unknown>;

  @ViewChild('statusTpl', {read: TemplateRef})
  statusTpl!: TemplateRef<unknown>;

  @ViewChild('durationTpl', {read: TemplateRef})
  durationTpl!: TemplateRef<unknown>;

  @ViewChild('tableRef') tableRef?: GateweeTableV2Component<TaskWithProgress>;

  activeTasks: TaskWithProgress[] = [];
  historyTasks: TaskWithProgress[] = [];
  columns: GateweeTableColumn[] = [];
  historyStatusFilter: HistoryStatusFilter = 'all';

  page = 1;
  pageSize = 10;
  total = 0;
  from = 0;

  readonly comingSoonHint = 'features.tasks.comingSoon';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly taskService: TaskService,
    private readonly reportService: ReportService,
    private readonly translate: TranslateService,
    private readonly alerts: TuiAlertService,
    private readonly dialogs: TuiDialogService,
    private readonly datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.fetchHistory();
    this.startActiveTasksPolling();
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        label: 'features.tasks.columns.task',
        field: '',
        template: this.taskTpl,
        alwaysVisible: true,
      },
      {
        label: 'features.tasks.columns.startDate',
        field: 'createdAt',
        alwaysVisible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd MMM yyyy, HH:mm'],
        },
      },
      {
        label: 'features.tasks.columns.duration',
        field: '',
        template: this.durationTpl,
        alwaysVisible: true,
      },
      {
        label: 'features.tasks.columns.status',
        field: 'status',
        template: this.statusTpl,
        alwaysVisible: true,
      },
      {
        label: 'features.tasks.columns.action',
        field: 'actions',
        alwaysVisible: true,
        actions: [
          {
            key: 'viewDetails',
            label: 'features.tasks.actions.viewDetails',
            icon: '@tui.eye',
            condition: {status: 'completed'},
          },
          {
            key: 'download',
            label: 'features.tasks.actions.download',
            icon: '@tui.download',
            condition: {status: 'completed', type: 'report'},
          },
          {
            key: 'retry',
            label: 'features.tasks.actions.retry',
            icon: '@tui.rotate-cw',
            condition: {status: 'failed'},
          },
        ],
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get resolvedBreadcrumb(): {label: string; path?: string}[] {
    if (this.breadcrumb.length) {
      return this.breadcrumb;
    }
    return [
      {label: 'nav.breadcrumb.home', path: '/panel'},
      {label: 'features.tasks.title'},
    ];
  }

  getActionDisabled = (row: TaskWithProgress, key: string): boolean => {
    if (key === 'download') {
      return !this.canDownload(row);
    }
    return false;
  };

  onNewExport(): void {
    this.alerts
      .open(this.translate.instant(this.comingSoonHint), {
        label: this.translate.instant('features.tasks.newExport'),
        appearance: 'info',
      })
      .subscribe();
  }

  setHistoryFilter(filter: HistoryStatusFilter): void {
    if (this.historyStatusFilter === filter) {
      return;
    }

    this.historyStatusFilter = filter;
    this.page = 1;
    this.from = 0;
    this.fetchHistory();
  }

  onPageChange(nextPage: number): void {
    this.page = nextPage;
    this.from = (nextPage - 1) * this.pageSize;
    this.fetchHistory();
  }

  onPageSizeChange(nextSize: number): void {
    this.pageSize = nextSize;
    this.page = 1;
    this.from = 0;
    this.fetchHistory();
  }

  onActionClicked(event: {row: TaskWithProgress; type: string}): void {
    const {row, type} = event;

    if (type === 'viewDetails' && row.status === 'completed') {
      this.openTaskDetails(row);
      return;
    }

    if (type === 'retry' && row.status === 'failed') {
      this.retryTask(row);
      return;
    }

    if (type === 'download' && this.canDownload(row)) {
      const reportId = this.getReportId(row);
      if (reportId === null) {
        return;
      }
      this.reportService.download(this.scope, reportId).subscribe({
        next: ({url}) => window.open(url, '_blank', 'noopener,noreferrer'),
        error: () => {
          this.alerts
            .open(this.translate.instant('features.tasks.errors.download'), {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            })
            .subscribe();
        },
      });
    }
  }

  getTaskIcon(type: TaskType): string {
    switch (type) {
      case 'report':
        return '@phi.file-csv';
      case 'webhook':
        return '@phi.webhooks-logo';
      case 'email':
        return '@phi.envelope';
      default:
        return '@phi.list-checks';
    }
  }

  getTaskProgress(task: TaskWithProgress): number {
    if (
      task.progress?.progress !== undefined &&
      task.progress?.progress !== null
    ) {
      return task.progress.progress;
    }
    return task.status === 'pending' ? 0 : 100;
  }

  getTaskEta(task: TaskWithProgress): string | null {
    const eta = task.progress?.meta?.['eta'];
    if (typeof eta !== 'string' || eta.length === 0) {
      return null;
    }
    if (/restante/i.test(eta)) {
      return eta;
    }
    return this.translate.instant('features.tasks.etaRemaining', {time: eta});
  }

  getStatusLabel(status: TaskStatus): string {
    return this.translate.instant(`features.tasks.status.${status}`);
  }

  getTaskTypeLabel(type: TaskType): string {
    return this.translate.instant(`features.tasks.types.${type}`);
  }

  getTaskLabel(task: TaskWithProgress): string {
    return getTaskLabel(task, (key, params) =>
      this.translate.instant(key, params),
    );
  }

  getTaskSubtitle(task: TaskWithProgress): string | undefined {
    return getTaskSubtitle(task, (key, params) =>
      this.translate.instant(key, params),
    );
  }

  isActiveStatus(status: TaskStatus): boolean {
    return status === 'pending' || status === 'processing';
  }

  getStatusBadgeClass(status: TaskStatus): string {
    switch (status) {
      case 'completed':
        return 'gw-bg-status-positive-pale gw-text-status-positive';
      case 'failed':
        return 'gw-bg-status-negative-pale gw-text-status-negative';
      case 'processing':
        return 'gw-bg-status-warning-pale gw-text-status-warning';
      default:
        return 'gw-bg-background-neutral-1 gw-text-text-secondary';
    }
  }

  private startActiveTasksPolling(): void {
    pollWhileVisible({intervalMs: 30_000})
      .pipe(
        switchMap(() =>
          this.taskService
            .getActiveTasks(this.scope)
            .pipe(catchError(() => of({records: [], count: 0}))),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({records}) => {
        this.activeTasks = records;
      });
  }

  private fetchHistory(): void {
    this.taskService
      .findAllPaginated(this.scope, this.from, this.pageSize, {
        where: {
          ...this.buildHistoryStatusFilter(),
        },
      })
      .pipe(catchError(() => of({records: [], count: 0})))
      .subscribe(({records, count}) => {
        this.historyTasks = records;
        this.total = count;
      });
  }

  private buildHistoryStatusFilter(): Record<string, unknown> {
    if (this.historyStatusFilter === 'completed') {
      return {status: 'completed'};
    }
    if (this.historyStatusFilter === 'failed') {
      return {status: 'failed'};
    }
    return {};
  }

  private canDownload(task: TaskWithProgress): boolean {
    return (
      task.status === 'completed' &&
      task.type === 'report' &&
      this.getReportId(task) !== null
    );
  }

  private getReportId(task: TaskWithProgress): number | null {
    const reportId = task.result?.['reportId'];
    return typeof reportId === 'number' ? reportId : null;
  }

  private openTaskDetails(task: TaskWithProgress): void {
    this.dialogs
      .open(new PolymorpheusComponent(GateweeAlertDialogComponent), {
        data: {
          title: this.getTaskLabel(task),
          message: this.buildTaskDetailsMessage(task),
          closeLabel: this.translate.instant('common.actions.ok'),
        },
        dismissible: true,
        size: 'm',
      })
      .subscribe();
  }

  private buildTaskDetailsMessage(task: TaskWithProgress): string {
    const lines: string[] = [
      `${this.translate.instant(
        'features.tasks.columns.status',
      )}: ${this.getStatusLabel(task.status)}`,
      `${this.translate.instant('features.tasks.columns.startDate')}: ${
        this.datePipe.transform(task.createdAt, 'dd/MM/yyyy HH:mm') ?? '—'
      }`,
    ];

    const subtitle = this.getTaskSubtitle(task);
    if (subtitle) {
      lines.push(subtitle);
    }

    const reportId = this.getReportId(task);
    if (reportId !== null) {
      lines.push(
        this.translate.instant('features.tasks.details.reportId', {
          id: reportId,
        }),
      );
    }

    return lines.join('\n\n');
  }

  private retryTask(task: TaskWithProgress): void {
    this.taskService.retry(this.scope, task.id).subscribe({
      next: () => {
        this.alerts
          .open(this.translate.instant('features.tasks.retrySuccess'), {
            label: this.translate.instant('common.status.success'),
            appearance: 'success',
          })
          .subscribe();
        this.fetchHistory();
        this.taskService
          .getActiveTasks(this.scope)
          .pipe(catchError(() => of({records: [], count: 0})))
          .subscribe(({records}) => {
            this.activeTasks = records;
          });
      },
      error: () => {
        this.alerts
          .open(this.translate.instant('features.tasks.errors.retry'), {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          })
          .subscribe();
      },
    });
  }
}
